import { Image } from 'expo-image';
import React, { useState } from 'react';
import {
  ImageSourcePropType,
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Button from 'shared/components/vxl/Button/Button';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import theme from 'shared/styles/theme';

export enum OfferCardState {
  DEFAULT = 'default',
  PRESSED = 'pressed',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  HOVER = 'hover',
  FOCUSED = 'focused',
}

export interface Copy {
  text: string;
}

export interface OfferItem {
  id: string;
  iconUrl: string;
  title: Copy;
}

export interface OfferCardProps {
  id: string;
  title: Copy;
  bodyItems: readonly OfferItem[];
  buttonTitle?: Copy;
  tag?: TagProps;
  onPress: () => void;
  onActivate: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
  accessibilityHint?: string;
  logoPosition?: 'overlap' | 'separate';
  showFewerLogos?: boolean;
}

export const OfferCard = ({
  id: _id,
  title,
  bodyItems,
  buttonTitle,
  tag,
  onPress,
  onActivate,
  isActive = false,
  isDisabled = false,
  style,
  accessibilityLabel,
  testID,
  accessibilityHint,
  logoPosition = 'overlap',
  showFewerLogos = false,
}: OfferCardProps) => {
  const [cardState, setCardState] = useState<OfferCardState>(() => {
    if (isDisabled) {
      return OfferCardState.DISABLED;
    }
    if (isActive) {
      return OfferCardState.ACTIVE;
    }
    return OfferCardState.DEFAULT;
  });

  const handlePressIn = () => {
    if (cardState !== OfferCardState.DISABLED) {
      setCardState(OfferCardState.PRESSED);
    }
  };

  const handlePressOut = () => {
    if (cardState !== OfferCardState.DISABLED) {
      setCardState(isActive ? OfferCardState.ACTIVE : OfferCardState.DEFAULT);
    }
  };

  const handleHoverIn = () => {
    if (
      cardState !== OfferCardState.DISABLED &&
      cardState !== OfferCardState.PRESSED
    ) {
      setCardState(OfferCardState.HOVER);
    }
  };

  const handleHoverOut = () => {
    if (
      cardState !== OfferCardState.DISABLED &&
      cardState !== OfferCardState.PRESSED
    ) {
      setCardState(isActive ? OfferCardState.ACTIVE : OfferCardState.DEFAULT);
    }
  };

  const handleFocus = () => {
    if (cardState !== OfferCardState.DISABLED) {
      setCardState(OfferCardState.FOCUSED);
    }
  };

  const handleBlur = () => {
    if (cardState !== OfferCardState.DISABLED) {
      setCardState(isActive ? OfferCardState.ACTIVE : OfferCardState.DEFAULT);
    }
  };

  const getContainerStyle = (): StyleProp<ViewStyle> => {
    const containerStyles: StyleProp<ViewStyle>[] = [styles.container];

    if (style) {
      containerStyles.push(style);
    }

    switch (cardState) {
      case OfferCardState.PRESSED:
        containerStyles.push(styles.containerPressed);
        break;
      case OfferCardState.ACTIVE:
        containerStyles.push(styles.containerActive);
        break;
      case OfferCardState.DISABLED:
        containerStyles.push(styles.containerDisabled);
        break;
      case OfferCardState.HOVER:
        containerStyles.push(styles.containerHover);
        break;
      case OfferCardState.FOCUSED:
        containerStyles.push(styles.containerFocused);
        break;
    }

    return containerStyles;
  };

  const renderCornerImage = () => {
    return (
      <View style={styles.cornerImageContainer}>
        <Image
          source={
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            require('assets/images/rewards/featuredOfferIcon.png') as ImageSourcePropType
          }
          style={styles.cornerImage}
          contentFit="cover"
          testID={`${testID}-corner-image`}
        />
      </View>
    );
  };
  const renderOfferItems = () => {
    if (!bodyItems || bodyItems.length === 0) {
      return null;
    }

    const itemsToShow = showFewerLogos ? 2 : 5;
    const displayItems = bodyItems.slice(0, itemsToShow);

    const containerStyle =
      logoPosition === 'separate'
        ? [styles.offerItemsContainer, styles.offerItemsContainerSeparate]
        : styles.offerItemsContainer;

    return (
      <View style={containerStyle}>
        {displayItems.map((item, index) => {
          const itemStyle =
            logoPosition === 'separate'
              ? styles.offerItemWrapper
              : [styles.offerItemWrapper, index > 0 && styles.offerItemOverlap];

          return (
            <View
              key={item.id}
              style={itemStyle}
              testID={`${testID}-offer-item-${index}`}
              accessibilityLabel={item.title.text}
            >
              <IconAvatar
                variant="MD"
                backgroundColor={theme.color.background.tertiary}
                defaultPlaceholderIcon="STORE"
                imageSource={{ uri: item.iconUrl }}
                iconAccessibilityLabel="offer-logo-image"
              />
            </View>
          );
        })}
        {bodyItems.length > itemsToShow ? (
          <View
            style={
              logoPosition === 'separate'
                ? styles.offerItemWrapper
                : [styles.offerItemWrapper, styles.offerItemOverlap]
            }
          >
            <View style={styles.moreItemsIndicator}>
              <BodyBaseText style={styles.moreItemsText}>
                +{bodyItems.length - itemsToShow}
              </BodyBaseText>
            </View>
          </View>
        ) : null}
      </View>
    );
  };

  return (
    <View style={{ padding: theme.spacing.m }}>
      <Pressable
        style={getContainerStyle()}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={isDisabled}
        accessible={true}
        accessibilityLabel={accessibilityLabel || `${title?.text} offer card`}
        accessibilityRole="button"
        accessibilityHint={
          accessibilityHint || 'Double tap to view offer details'
        }
        accessibilityState={{
          disabled: cardState === OfferCardState.DISABLED,
          selected: cardState === OfferCardState.ACTIVE,
        }}
        testID={testID}
      >
        {renderCornerImage()}
        {renderOfferItems()}

        <View style={styles.contentContainer}>
          {tag ? <Tag text={tag.text} type={tag.type} /> : null}

          <TitleLgText numberOfLines={3} style={styles.titleStyle}>
            {title?.text}
          </TitleLgText>

          <View style={styles.buttonContainer}>
            <Button
              type="PRIMARY"
              label={buttonTitle?.text ?? 'Activate'}
              onPress={onActivate}
              size="SMALL"
              width="HUG"
              isDisabled={false}
            />
          </View>
        </View>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.expressive.orchid,
    borderRadius: theme.spacing.xs,
    elevation: 3,
    overflow: 'hidden',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.l,
    position: 'relative',
    width: '100%',
  },
  containerActive: {
    borderColor: theme.color.interactive.primary,
    borderWidth: 2,
  },
  containerDisabled: {
    opacity: 0.5,
  },
  containerFocused: {
    borderColor: theme.color.border.focused,
    borderRadius: 4,
    borderWidth: 1,
  },
  containerHover: {
    backgroundColor: '#E4D9FC',
  },
  containerPressed: {
    backgroundColor: '#DDD0FB',
    opacity: 0.8,
    transform: [{ scale: 0.98 }],
  },
  contentContainer: {
    marginTop: theme.spacing.m,
  },
  cornerImage: {
    height: 119,
    width: 240,
  },
  cornerImageContainer: {
    position: 'absolute',
    right: 0,
    top: -20,
    zIndex: -1,
  },
  moreItemsIndicator: {
    alignItems: 'center',
    backgroundColor: theme.color.background.secondary,
    borderRadius: theme.spacing.m,
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  moreItemsText: {
    color: theme.color.text.secondary,
    fontSize: 10,
    fontWeight: '600',
  },
  offerItemOverlap: {
    marginLeft: -12,
  },
  offerItemWrapper: {
    borderRadius: theme.spacing.xs,
    height: theme.spacing.xxl,
    width: theme.spacing.xxl,
  },
  offerItemsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    height: theme.spacing.xl,
    justifyContent: 'flex-start',
    marginBottom: theme.spacing.xl,
    paddingTop: theme.spacing.l,
    zIndex: 10,
  },
  offerItemsContainerSeparate: {
    gap: theme.spacing.xs,
  },
  titleStyle: {
    marginVertical: theme.spacing.s,
  },
});

export default OfferCard;
